import React, { useState, useEffect, useRef } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { PaginationSearchProps } from './types'

/**
 * Componente de búsqueda para la paginación
 * Incluye debounce para optimizar búsquedas
 */
const PaginationSearch: React.FC<PaginationSearchProps> = ({
  searchTerm,
  onSearchChange,
  debounceMs = 300,
  isLoading = false,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm)
  const onSearchChangeRef = useRef(onSearchChange)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastSentValueRef = useRef<string>(searchTerm)
  const isUserTypingRef = useRef(false)

  // Actualizar ref cuando cambie el callback
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange
  }, [onSearchChange])

  // Sincronizar con prop externo solo cuando el cambio viene de fuera
  useEffect(() => {
    const isFromOurDebounce = searchTerm === lastSentValueRef.current
    
    // Solo sincronizar si:
    // 1. El cambio NO viene de nuestro debounce
    // 2. El valor es diferente al local
    // 3. El usuario NO está escribiendo activamente (o el input no está enfocado)
    if (!isFromOurDebounce && searchTerm !== localSearchTerm && !isUserTypingRef.current) {
      setLocalSearchTerm(searchTerm)
      lastSentValueRef.current = searchTerm
    } else if (isFromOurDebounce) {
      // Si viene de nuestro debounce, actualizar la ref pero no cambiar el estado
      lastSentValueRef.current = searchTerm
    }
  }, [searchTerm, localSearchTerm])

  // Debounce del término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        lastSentValueRef.current = localSearchTerm
        onSearchChangeRef.current(localSearchTerm)
        // Resetear el flag después de enviar
        setTimeout(() => {
          isUserTypingRef.current = false
        }, 50)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [localSearchTerm, debounceMs, searchTerm])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isUserTypingRef.current = true
    setLocalSearchTerm(e.target.value)
  }

  const handleBlur = () => {
    // Resetear el flag cuando el usuario deja de escribir
    setTimeout(() => {
      isUserTypingRef.current = false
    }, 100)
  }

  return (
    <div className="relative w-48 sm:w-64">
      <input
        ref={inputRef}
        type="text"
        placeholder="Buscar..."
        value={localSearchTerm}
        onChange={handleChange}
        onBlur={handleBlur}
        className="w-full px-3 py-1.5 pl-9 text-sm border border-gray-200 rounded-lg 
                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                   transition-all duration-200"
        aria-label="Buscar en la tabla"
      />
      <MagnifyingGlassIcon 
        className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none" 
      />
    </div>
  )
}

export default React.memo(PaginationSearch)

